// src/components/layout/BreakControl.jsx
// Control de pausa (comida / break) para agentes. Se muestra en el Topbar.
// Al iniciar pausa, el backend excluye al agente del round-robin hasta que
// expira el tiempo o el agente la finaliza manualmente.
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';
import socket from '../../services/socket';


// ── Iconos SVG inline ────────────────────────────────────────────────────────
const Icon = ({ children, size = 14, stroke = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {children}
    </svg>
);
const I = {
    available: <Icon><circle cx="12" cy="12" r="9" /><path d="m9 12 2 2 4-4" /></Icon>,
    pause:     <Icon><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></Icon>,
    coffee:    <Icon><path d="M17 8h1a4 4 0 1 1 0 8h-1" /><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z" /><path d="M6 1v3M10 1v3M14 1v3" /></Icon>,
    fork:      <Icon><path d="M3 2v6c0 1.1.9 2 2 2h2v10" /><path d="M21 2v20" /><path d="M21 9c-3 0-3-3-3-3V2" /></Icon>,
    edit:      <Icon><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" /></Icon>,
    chevron:   <Icon size={12}><path d="m6 9 6 6 6-6" /></Icon>,
    stop:      <Icon><rect x="6" y="6" width="12" height="12" rx="2" /></Icon>,
};


// ── Hook click-outside ──────────────────────────────────────────────────────
function useClickOutside(ref, onOutside, when = true) {
    useEffect(() => {
        if (!when) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onOutside();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [ref, onOutside, when]);
}


function formatRemaining(ms) {
    if (ms <= 0) return '0:00';
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}


export default function BreakControl() {
    const { user } = useContext(AuthContext);
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [now, setNow]         = useState(Date.now());
    const [customOpen, setCustomOpen] = useState(false);
    const [customMin, setCustomMin]   = useState(15);
    const [menuOpen, setMenuOpen] = useState(false);
    const tickRef = useRef(null);
    const wrapRef = useRef(null);

    useClickOutside(wrapRef, () => setMenuOpen(false), menuOpen);

    // Admin light trae role='admin' (efectivo) pero opera como agente: también toma pausas.
    const isAgent = user?.role === 'agent' || !!user?.adminLite;

    useEffect(() => {
        if (!isAgent) return;
        let alive = true;
        const load = async () => {
            try {
                const { data } = await api.get('/users/me/break');
                if (alive) setState(data);
            } catch { /* silent */ }
        };
        load();

        const onChanged = (payload) => {
            if (String(payload.userId) === String(user.id)) load();
        };
        socket.on('userBreakChanged', onChanged);
        return () => { alive = false; socket.off('userBreakChanged', onChanged); };
    }, [isAgent, user?.id]);

    useEffect(() => {
        if (!state?.break?.active) {
            if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
            return;
        }
        tickRef.current = setInterval(() => setNow(Date.now()), 1000);
        return () => { if (tickRef.current) clearInterval(tickRef.current); };
    }, [state?.break?.active]);

    useEffect(() => {
        if (!state?.break?.active) return;
        const exp = new Date(state.break.expiresAt).getTime();
        if (now >= exp) {
            api.get('/users/me/break').then(({ data }) => setState(data)).catch(() => {});
        }
    }, [now, state?.break?.active, state?.break?.expiresAt]);

    if (!isAgent) return null;
    if (!state)   return null;

    const { break: brk, breakUsage, config } = state;
    if (!config?.enabled) return null;

    const lunchRemaining = Math.max(0, (config.lunchPerDay || 0) - (breakUsage?.lunchUsed || 0));
    const shortRemaining = Math.max(0, (config.shortPerDay || 0) - (breakUsage?.shortUsed || 0));

    const endBreak = async () => {
        setLoading(true);
        try {
            const { data } = await api.delete('/users/me/break');
            setState(s => ({ ...s, ...data }));
            toast.success('Pausa finalizada');
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo finalizar la pausa');
        } finally { setLoading(false); }
    };

    // ── Estado en pausa: pill amber + botón "Finalizar" ──────────────────────
    if (brk?.active) {
        const ms = new Date(brk.expiresAt).getTime() - now;
        const label = brk.type === 'lunch' ? 'Comida' : 'Pausa';
        return (
            <>
                <style>{BREAK_CSS}</style>
                <div className="bk-active-group">
                    <div className="bk-active-pill" title={`${label} — termina en ${formatRemaining(ms)}`}>
                        {I.pause}
                        <span><span className="bk-label">{label} </span><strong>{formatRemaining(ms)}</strong></span>
                    </div>
                    <button
                        type="button"
                        className="bk-end-btn"
                        onClick={endBreak}
                        disabled={loading}
                        title="Finalizar pausa"
                        aria-label="Finalizar pausa"
                    >
                        {loading ? <Spinner size="sm" animation="border" /> : <>{I.stop}<span className="bk-label"> Finalizar</span></>}
                    </button>
                </div>
            </>
        );
    }

    const startBreak = async (type, durationMinutes) => {
        setLoading(true);
        try {
            const body = { type };
            if (durationMinutes != null) body.durationMinutes = durationMinutes;
            const { data } = await api.post('/users/me/break', body);
            setState(s => ({ ...s, ...data }));
            toast.success(type === 'lunch' ? 'Comida iniciada' : 'Pausa iniciada');
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo iniciar la pausa');
        } finally { setLoading(false); setMenuOpen(false); }
    };

    return (
        <>
            <style>{BREAK_CSS}</style>

            <div className="bk-wrap" ref={wrapRef}>
                <button
                    type="button"
                    className="bk-trigger"
                    onClick={() => setMenuOpen(v => !v)}
                    disabled={loading}
                    title="Tomar pausa (retira del round-robin)"
                    aria-expanded={menuOpen}
                >
                    <span className="bk-dot" />
                    <span className="bk-label">Disponible</span>
                    <span className="bk-chev">{I.chevron}</span>
                </button>

                {menuOpen && (
                    <div className="bk-menu">
                        <p className="bk-menu-header">
                            Hoy: {lunchRemaining}/{config.lunchPerDay} comida · {shortRemaining}/{config.shortPerDay} pausas
                        </p>
                        <button
                            type="button"
                            className="bk-menu-item"
                            disabled={lunchRemaining <= 0}
                            onClick={() => startBreak('lunch')}
                        >
                            {I.fork} Comida <span className="bk-meta">({config.lunchMinutes} min)</span>
                        </button>
                        <button
                            type="button"
                            className="bk-menu-item"
                            disabled={shortRemaining <= 0}
                            onClick={() => startBreak('short')}
                        >
                            {I.coffee} Pausa corta <span className="bk-meta">({config.shortMinutes} min)</span>
                        </button>
                        <div className="bk-menu-divider" />
                        <button
                            type="button"
                            className="bk-menu-item"
                            disabled={shortRemaining <= 0}
                            onClick={() => {
                                setCustomMin(Math.min(15, config.shortMinutes));
                                setCustomOpen(true);
                                setMenuOpen(false);
                            }}
                        >
                            {I.edit} Personalizada…
                        </button>
                    </div>
                )}
            </div>

            <Modal show={customOpen} onHide={() => setCustomOpen(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>Pausa personalizada</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-2)', display: 'block', marginBottom: 6 }}>
                        Duración (minutos)
                    </label>
                    <input
                        type="number"
                        min={1}
                        max={config.shortMinutes}
                        value={customMin}
                        onChange={e => setCustomMin(Number(e.target.value))}
                        style={{
                            width: '100%', padding: '9px 12px', fontSize: 14,
                            border: '1px solid var(--c-border)', borderRadius: 8,
                            background: 'var(--c-input-bg)', color: 'var(--c-text-1)',
                            fontFamily: 'inherit', outline: 'none',
                        }}
                    />
                    <p style={{ fontSize: 12, color: 'var(--c-text-4)', marginTop: 6, marginBottom: 0 }}>
                        Cuenta como pausa corta. Máximo {config.shortMinutes} min.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" size="sm" onClick={() => setCustomOpen(false)} style={{ borderRadius: 8 }}>Cancelar</Button>
                    <Button
                        variant="primary" size="sm"
                        disabled={loading || customMin < 1}
                        onClick={() => startBreak('other', customMin)}
                        style={{ borderRadius: 8, fontWeight: 600 }}
                    >
                        Iniciar pausa
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}


const BREAK_CSS = `
.bk-wrap { position: relative; }
.bk-trigger {
    display: inline-flex; align-items: center; gap: 7px;
    background: rgba(255,255,255,.08);
    border: 1px solid rgba(255,255,255,.14);
    color: #fff;
    border-radius: 999px;
    padding: 6px 12px 6px 11px;
    font-size: 12.5px; font-weight: 600;
    cursor: pointer;
    transition: background .15s, border-color .15s;
    font-family: inherit; white-space: nowrap;
}
.bk-trigger:hover {
    background: rgba(255,255,255,.16);
    border-color: rgba(255,255,255,.24);
}
.bk-trigger:disabled { opacity: 0.5; cursor: not-allowed; }
.bk-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: #22c55e;
    box-shadow: 0 0 0 2px rgba(34, 197, 94, .25);
    flex-shrink: 0;
}
.bk-chev { opacity: .55; display: inline-flex; }

.bk-menu {
    position: absolute; top: calc(100% + 6px); right: 0;
    background: var(--c-card);
    border: 1px solid var(--c-border);
    border-radius: 12px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, .18), 0 4px 12px rgba(15, 23, 42, .08);
    min-width: 240px;
    padding: 6px;
    z-index: 1050;
    animation: bk-pop .14s ease-out both;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}
@keyframes bk-pop {
    from { opacity: 0; transform: translateY(-4px) scale(.98); }
    to   { opacity: 1; transform: none; }
}
.bk-menu-header {
    font-size: 10px; font-weight: 700; text-transform: uppercase;
    letter-spacing: .6px; color: var(--c-text-4);
    padding: 8px 12px 6px; margin: 0;
}
.bk-menu-item {
    display: flex; align-items: center; gap: 10px;
    width: 100%; padding: 9px 12px;
    border-radius: 8px; border: none; background: transparent;
    color: var(--c-text-1); text-align: left; cursor: pointer;
    font-size: 13.5px; font-family: inherit;
    transition: background .12s;
}
.bk-menu-item:hover:not(:disabled) { background: var(--c-msg-bg); }
.bk-menu-item:disabled { opacity: 0.4; cursor: not-allowed; }
.bk-meta { color: var(--c-text-4); font-size: 12px; margin-left: auto; }
.bk-menu-divider { height: 1px; background: var(--c-card-header-border); margin: 6px 4px; }

/* En pausa */
.bk-active-group { display: inline-flex; align-items: center; gap: 6px; }
.bk-active-pill {
    display: inline-flex; align-items: center; gap: 7px;
    background: #f59e0b; color: #fff;
    border: 1px solid #d97706;
    border-radius: 999px;
    padding: 6px 12px;
    font-size: 12.5px; font-weight: 600;
    white-space: nowrap;
}
.bk-active-pill strong { font-variant-numeric: tabular-nums; }
.bk-end-btn {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(255,255,255,.08);
    border: 1px solid rgba(255,255,255,.14);
    color: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-size: 12.5px; font-weight: 600;
    cursor: pointer;
    transition: background .15s, border-color .15s;
    font-family: inherit; white-space: nowrap;
}
.bk-end-btn:hover:not(:disabled) {
    background: rgba(255,255,255,.16);
    border-color: rgba(255,255,255,.24);
}
.bk-end-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Teléfono: solo el punto/ícono y el contador; el texto no cabe en la barra */
@media (max-width: 520px) {
    .bk-label { display: none; }
    .bk-trigger { padding: 8px 9px; gap: 4px; }
    .bk-active-pill { padding: 6px 10px; }
    .bk-end-btn { padding: 7px 9px; }
    .bk-menu { position: fixed; left: 8px; right: 8px; top: 56px; min-width: 0; }
}
`;
